import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { get, post, toastError } from "@/lib/api";
import { fmtDate, todayISO, addDaysISO, num } from "@/lib/format";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, ArrowRight, MousePointer2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { dayLabel, ROOM_DOT } from "./AccommodationBits";

/* ------------------------------------------------------------------ geometry ---- */
const ZOOM = { 14: 56, 28: 30, 56: 17 }; // minimum px per day – stretched to fill the container when wider
const LEFT = 168;
const ROW_H = 30;
const HEAD_H = 26;
const WEEKDAY = ["M", "T", "O", "T", "F", "L", "S"];

/** Width of the scroll container (callback-ref based, so it also works when the container mounts after loading). */
const useContainerWidth = () => {
  const [node, setNode] = useState(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => setW(Math.floor(entries[0]?.contentRect?.width || 0)));
    ro.observe(node);
    setW(Math.floor(node.clientWidth));
    return () => ro.disconnect();
  }, [node]);
  return [setNode, w];
};

const diffDays = (a, b) => Math.round((new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z")) / 86_400_000);
const mondayOf = (iso) => { const d = new Date(iso + "T00:00:00Z"); const wd = (d.getUTCDay() + 6) % 7; return addDaysISO(iso, -wd); };
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const BAR = {
  innsjekket: "bg-foreground/85 text-background border border-foreground/85",
  planlagt: "bar-planned bg-background text-foreground border border-foreground/40",
  utsjekket: "bg-muted text-muted-foreground/80 border border-border",
};
const stayLabel = (s) => s.person_name || "Opphold";
const stayTitle = (s) => `${stayLabel(s)}${s.employer_name ? ` · ${s.employer_name}` : ""}\n${s.operational_number} seng ${s.bed_index} · ${dayLabel(s.start)} – ${dayLabel(s.end)}\n${s.status}${s.overdue_in ? " · forsinket ankomst" : ""}${s.overdue_out ? " · utsjekk forfalt" : ""}`;

/* ------------------------------------------------------------------ confirm dialog ---- */
const RescheduleDialog = ({ pending, onOpenChange, onConfirm, busy }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (pending) setReason(""); }, [pending]);
  if (!pending) return null;
  const { stay, to, roomChanged, datesChanged } = pending;
  return (
    <AlertDialog open={Boolean(pending)} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="calendar-reschedule-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{roomChanged && datesChanged ? "Flytt og endre periode" : roomChanged ? "Flytt til annet rom" : "Endre periode"}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-[13px] text-foreground/85">
              <div className="font-medium text-foreground">{stayLabel(stay)}{stay.employer_name ? <span className="font-normal text-muted-foreground"> · {stay.employer_name}</span> : null}</div>
              {roomChanged && (
                <div className="flex items-center gap-2 font-mono tabular" data-testid="calendar-reschedule-room-diff">
                  <span className="text-muted-foreground line-through">{stay.operational_number} seng {stay.bed_index}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-semibold">{to.operational_number} seng {to.bed_index}</span>
                </div>
              )}
              {datesChanged && (
                <div className="flex items-center gap-2 tabular" data-testid="calendar-reschedule-dates-diff">
                  <span className="text-muted-foreground line-through">{dayLabel(stay.planned_in)} – {dayLabel(stay.planned_out)}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-semibold">{dayLabel(to.planned_in)} – {dayLabel(to.planned_out)}</span>
                  <span className="text-muted-foreground">({diffDays(to.planned_in, to.planned_out)} netter)</span>
                </div>
              )}
              {stay.status === "innsjekket" && roomChanged && <p className="text-[12px] text-muted-foreground">Personen er innsjekket – det gamle rommet merkes «må rengjøres» når det blir tomt.</p>}
              <div className="space-y-1.5 pt-1">
                <Label className="text-[12px] text-muted-foreground">Årsak (valgfritt)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="f.eks. ønsker rom nær kollega, feil på bad, utvidet skift" data-testid="calendar-reschedule-reason" />
              </div>
              <p className="text-[12px] text-muted-foreground">Ingenting er lagret ennå. Endringen valideres på nytt mot kollisjoner og romstatus, og logges i audit.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="calendar-reschedule-cancel">Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(reason); }} disabled={busy} data-testid="calendar-reschedule-confirm">{busy ? <Loader2 className="animate-spin" /> : null} Bekreft</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Room calendar – rooms × beds as lanes, stays as bars, 2/4/8 weeks.
 * Drag a bar vertically to another room/bed, horizontally to shift a planned stay, or drag its right edge to change
 * departure. Drag on an empty lane to register a new stay. Nothing is written until the user confirms the diff.
 * Touch devices are read-only (scroll + inspect); every write is re-validated server-side (409 on collisions).
 */
export const RoomCalendar = ({ projectId, canWrite, hasPersons, onOpenRoom, onCreateStay }) => {
  const qc = useQueryClient();
  const fine = useMediaQuery("(pointer: fine)");
  const wide = useMediaQuery("(min-width: 1024px)");
  const today = todayISO();
  const [start, setStart] = useState(() => mondayOf(today));
  const [days, setDays] = useState(wide ? 28 : 14);
  const [building, setBuilding] = useState("alle");
  const [drag, setDrag] = useState(null);       // live drag state (ghost)
  const [create, setCreate] = useState(null);   // drag-to-create selection
  const [pending, setPending] = useState(null); // confirm dialog payload
  const dragRef = useRef(null);
  const createRef = useRef(null);
  const editable = Boolean(canWrite && hasPersons && fine);
  const [scrollRef, containerW] = useContainerWidth();
  // days stretch to fill the container, never narrower than the zoom minimum (then the grid scrolls horizontally)
  const DAY = Math.max(ZOOM[days], containerW ? Math.floor((containerW - LEFT - 2) / days) : 0);

  const q = useQuery({ queryKey: ["ink", "calendar", projectId, start, days], queryFn: () => get(`/innkvartering/projects/${projectId}/calendar`, { start, days }), placeholderData: keepPreviousData });
  const d = q.data;

  /* rows = room × bed lanes, grouped by building */
  const { lanes, groups, buildings } = useMemo(() => {
    const rows = (d?.rows || []).filter((r) => building === "alle" || String(r.building) === building);
    const lanes = [];
    const groups = [];
    let cur = null;
    rows.forEach((r) => {
      if (!cur || cur.building !== r.building) { cur = { building: r.building, lanes: [] }; groups.push(cur); }
      for (let b = 1; b <= r.beds; b++) {
        const lane = { index: lanes.length, room: r, bed: b, key: `${r.room_id}:${b}` };
        lanes.push(lane); cur.lanes.push(lane);
      }
    });
    return { lanes, groups, buildings: [...new Set((d?.rows || []).map((r) => String(r.building)))].sort() };
  }, [d, building]);
  const laneByKey = useMemo(() => new Map(lanes.map((l) => [l.key, l])), [lanes]);
  const staysByLane = useMemo(() => {
    const m = new Map();
    (d?.stays || []).forEach((s) => { const k = `${s.room_id}:${s.bed_index}`; if (!m.has(k)) m.set(k, []); m.get(k).push(s); });
    return m;
  }, [d]);

  const dayIndex = (iso) => diffDays(start, iso);
  const x = (iso) => clamp(dayIndex(iso), 0, days) * DAY;
  const width = days * DAY;
  const todayIdx = dayIndex(today);

  /* header: ISO weeks */
  const weeks = useMemo(() => {
    const out = [];
    for (let i = 0; i < days; i += 7) out.push({ start: addDaysISO(start, i), label: `U${fmtDate(addDaysISO(start, i), "I")}`, month: fmtDate(addDaysISO(start, i), "MMM") });
    return out;
  }, [start, days]);

  /* ------------------------------------------------------------- drag: move / resize ---- */
  const laneAt = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY)?.closest?.("[data-lane-index]");
    return el ? Number(el.getAttribute("data-lane-index")) : null;
  };
  const onBarPointerDown = (e, stay, mode) => {
    if (!editable || e.pointerType === "touch" || e.button !== 0 || stay.status === "utsjekket") return;
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const lane = laneByKey.get(`${stay.room_id}:${stay.bed_index}`);
    dragRef.current = { stay, mode, startX: e.clientX, startY: e.clientY, origLane: lane?.index ?? null, moved: false };
    setDrag({ stay, mode, dStart: 0, dEnd: 0, lane: lane?.index ?? null, conflict: false });
  };
  const onBarPointerMove = (e) => {
    const r = dragRef.current;
    if (!r) return;
    const dx = e.clientX - r.startX;
    const dDays = Math.round(dx / DAY);
    let dStart = 0, dEnd = 0, lane = r.origLane;
    if (r.mode === "move") {
      lane = laneAt(e.clientX, e.clientY) ?? r.origLane;
      if (r.stay.status === "planlagt") { dStart = dDays; dEnd = dDays; }
    } else if (r.mode === "resize-end") dEnd = dDays;
    else if (r.mode === "resize-start") dStart = dDays;
    if (Math.abs(dx) > 4 || Math.abs(e.clientY - r.startY) > 4) r.moved = true;
    const to = proposal(r.stay, lane, dStart, dEnd);
    setDrag({ stay: r.stay, mode: r.mode, dStart, dEnd, lane, conflict: to ? hasConflict(r.stay, to) : true, to });
  };
  const proposal = (stay, laneIdx, dStart, dEnd) => {
    const lane = lanes[laneIdx];
    if (!lane) return null;
    const minIn = stay.status === "innsjekket" ? stay.planned_in : today;
    const planned_in = stay.status === "innsjekket" ? stay.planned_in : (addDaysISO(stay.planned_in, dStart) < minIn ? minIn : addDaysISO(stay.planned_in, dStart));
    let planned_out = addDaysISO(stay.planned_out, dEnd);
    if (planned_out <= planned_in) planned_out = addDaysISO(planned_in, 1);
    if (stay.status === "innsjekket" && planned_out < today) planned_out = today;
    return { room_id: lane.room.room_id, bed_index: lane.bed, operational_number: lane.room.operational_number, room_status: lane.room.room_status, planned_in, planned_out };
  };
  const hasConflict = (stay, to) => {
    if (to.room_status === "ute_av_drift") return true;
    const others = (staysByLane.get(`${to.room_id}:${to.bed_index}`) || []).filter((s) => s.id !== stay.id && s.status !== "utsjekket");
    const a = stay.status === "innsjekket" ? today : to.planned_in;
    return others.some((s) => s.planned_in < to.planned_out && s.planned_out > a);
  };
  const onBarPointerUp = () => {
    const r = dragRef.current;
    dragRef.current = null;
    const live = drag;
    setDrag(null);
    if (!r) return;
    if (!r.moved) { onOpenRoom?.(r.stay.room_id); return; }
    const to = live?.to || proposal(r.stay, live?.lane ?? r.origLane, live?.dStart || 0, live?.dEnd || 0);
    if (!to) return;
    const roomChanged = to.room_id !== r.stay.room_id || to.bed_index !== r.stay.bed_index;
    const datesChanged = to.planned_in !== r.stay.planned_in || to.planned_out !== r.stay.planned_out;
    if (!roomChanged && !datesChanged) return;
    if (to.room_status === "ute_av_drift") { toast.error(`${to.operational_number} er ute av drift`); return; }
    if (hasConflict(r.stay, to)) { toast.error(`Seng ${to.bed_index} i ${to.operational_number} er opptatt i perioden`, { description: "Velg en ledig seng, eller endre datoene først." }); return; }
    setPending({ stay: r.stay, to, roomChanged, datesChanged });
  };

  const m = useMutation({
    mutationFn: ({ stay, to, reason }) => post(`/innkvartering/stays/${stay.id}/reschedule`, { room_id: to.room_id, bed_index: to.bed_index, planned_in: to.planned_in, planned_out: to.planned_out, reason: reason || null }),
    onSuccess: (res, v) => {
      toast.success(v.roomChanged ? `${stayLabel(v.stay)} flyttet til ${v.to.operational_number}` : `Periode oppdatert for ${stayLabel(v.stay)}`, { description: v.datesChanged ? `${dayLabel(v.to.planned_in)} – ${dayLabel(v.to.planned_out)}` : undefined });
      qc.invalidateQueries({ queryKey: ["ink"] });
      setPending(null);
    },
    onError: (e) => toastError(e, "Kunne ikke endre oppholdet"),
  });

  /* ------------------------------------------------------------- drag-to-create ---- */
  const onLanePointerDown = (e, lane) => {
    if (!editable || e.pointerType === "touch" || e.button !== 0 || lane.room.room_status === "ute_av_drift") return;
    if (e.target.closest("[data-stay-bar]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = clamp(Math.floor((e.clientX - rect.left) / DAY), 0, days - 1);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    createRef.current = { lane, anchor: idx, rect };
    setCreate({ lane: lane.index, from: idx, to: idx });
  };
  const onLanePointerMove = (e) => {
    const c = createRef.current;
    if (!c) return;
    const idx = clamp(Math.floor((e.clientX - c.rect.left) / DAY), 0, days - 1);
    setCreate({ lane: c.lane.index, from: Math.min(c.anchor, idx), to: Math.max(c.anchor, idx) });
  };
  const onLanePointerUp = () => {
    const c = createRef.current;
    const sel = create;
    createRef.current = null;
    setCreate(null);
    if (!c || !sel) return;
    const planned_in = addDaysISO(start, sel.from);
    const planned_out = addDaysISO(start, sel.to + 1);
    if (planned_in < today) { toast.error("Kan ikke planlegge opphold tilbake i tid"); return; }
    const taken = (staysByLane.get(c.lane.key) || []).some((s) => s.status !== "utsjekket" && s.planned_in < planned_out && s.planned_out > planned_in);
    if (taken) { toast.error(`Seng ${c.lane.bed} i ${c.lane.room.operational_number} er opptatt i perioden`); return; }
    onCreateStay?.({ room_id: c.lane.room.room_id, bed_index: c.lane.bed, operational_number: c.lane.room.operational_number, planned_in, planned_out });
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { dragRef.current = null; createRef.current = null; setDrag(null); setCreate(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { setDays((v) => (wide ? v : Math.min(v, 28))); }, [wide]);

  /* ------------------------------------------------------------- render ---- */
  const nav = (
    <div className="flex flex-wrap items-center gap-2" data-testid="calendar-toolbar">
      <div className="inline-flex items-center rounded-md border">
        <Button variant="ghost" size="sm" className="h-8 w-8 rounded-r-none px-0" onClick={() => setStart((s) => addDaysISO(s, -7))} aria-label="Forrige uke" data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" className="h-8 rounded-none border-x px-2.5 text-[12.5px]" onClick={() => setStart(mondayOf(today))} data-testid="calendar-today-button"><CalendarDays className="h-3.5 w-3.5" /> I dag</Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 rounded-l-none px-0" onClick={() => setStart((s) => addDaysISO(s, 7))} aria-label="Neste uke" data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <span className="text-[13px] font-medium tabular" data-testid="calendar-range-label">{dayLabel(start)} – {dayLabel(addDaysISO(start, days - 1))}</span>
      <div className="ml-auto flex items-center gap-2">
        {buildings.length > 1 && (
          <Select value={building} onValueChange={setBuilding}>
            <SelectTrigger className="h-8 w-[130px] text-[12.5px]" data-testid="calendar-building-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="alle">Alle bygg</SelectItem>{buildings.map((b) => <SelectItem key={b} value={b}>Bygg {b}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <ToggleGroup type="single" value={String(days)} onValueChange={(v) => v && setDays(Number(v))} className="h-8 rounded-md border p-0.5" data-testid="calendar-zoom">
          <ToggleGroupItem value="14" className="h-7 px-2.5 text-[12px]" data-testid="calendar-zoom-14">2 uker</ToggleGroupItem>
          <ToggleGroupItem value="28" className="h-7 px-2.5 text-[12px]" data-testid="calendar-zoom-28">4 uker</ToggleGroupItem>
          {wide && <ToggleGroupItem value="56" className="h-7 px-2.5 text-[12px]" data-testid="calendar-zoom-56">8 uker</ToggleGroupItem>}
        </ToggleGroup>
      </div>
    </div>
  );

  if (q.isLoading && !d) return <div className="space-y-3">{nav}<Skeleton className="h-[420px]" data-testid="calendar-loading" /></div>;
  if (q.isError) return <div className="space-y-3">{nav}<div className="rounded-lg border border-dashed p-6 text-center text-[13px] text-muted-foreground">Kunne ikke laste kalenderen. <Button variant="link" className="h-auto px-1" onClick={() => q.refetch()}>Prøv igjen</Button></div></div>;

  const strip = d.strip;
  const dayCells = strip.map((c, i) => (
    <div key={c.date} className={cn("absolute inset-y-0 border-r border-border/50", c.weekend && "bg-muted/50", c.today && "bg-primary/[0.05]")} style={{ left: i * DAY, width: DAY }} aria-hidden />
  ));

  return (
    <div className="space-y-3" data-testid="room-calendar">
      {nav}
      <div ref={scrollRef} className={cn("relative overflow-auto rounded-lg border bg-card", drag && "select-none cursor-grabbing")} style={{ maxHeight: "70vh" }} data-testid="calendar-scroll">
        <div style={{ width: LEFT + width }} className="relative">
          {/* header */}
          <div className="sticky top-0 z-30 bg-card/95 backdrop-blur" data-testid="calendar-header">
            <div className="flex" style={{ height: HEAD_H }}>
              <div className="sticky left-0 z-40 flex items-center border-b border-r bg-card px-3 text-[11px] uppercase tracking-wide text-muted-foreground" style={{ width: LEFT, minWidth: LEFT }}>Rom · seng</div>
              <div className="relative border-b" style={{ width, height: HEAD_H }}>
                {weeks.map((w, i) => (
                  <div key={w.start} className="absolute inset-y-0 flex items-center gap-1.5 border-r border-border/60 px-2 text-[11.5px]" style={{ left: i * 7 * DAY, width: Math.min(7, days - i * 7) * DAY }} data-testid={`calendar-week-${w.label}`}>
                    <span className="font-semibold">{w.label}</span><span className="truncate text-muted-foreground">{dayLabel(w.start)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex" style={{ height: HEAD_H }}>
              <div className="sticky left-0 z-40 border-b border-r bg-card" style={{ width: LEFT, minWidth: LEFT }} />
              <div className="relative border-b" style={{ width, height: HEAD_H }}>
                {strip.map((c, i) => (
                  <div key={c.date} className={cn("absolute inset-y-0 flex items-center justify-center border-r border-border/40 font-mono text-[10.5px] tabular", c.weekend && "bg-muted/50 text-muted-foreground", c.today && "bg-primary/[0.08] font-semibold text-primary")} style={{ left: i * DAY, width: DAY }} title={fmtDate(c.date, "EEEE d. MMMM")}>
                    {DAY >= 30 ? <span>{WEEKDAY[(new Date(c.date + "T00:00:00Z").getUTCDay() + 6) % 7]} {Number(c.date.slice(8))}</span> : <span>{Number(c.date.slice(8))}</span>}
                  </div>
                ))}
              </div>
            </div>
            {/* availability strip */}
            <div className="flex" style={{ height: HEAD_H }} data-testid="calendar-availability-strip">
              <div className="sticky left-0 z-40 flex items-center gap-1.5 border-b border-r bg-card px-3 text-[11.5px] text-muted-foreground" style={{ width: LEFT, minWidth: LEFT }}>Ledige senger</div>
              <div className="relative border-b bg-muted/20" style={{ width, height: HEAD_H }}>
                {strip.map((c, i) => {
                  const tone = c.free === 0 ? "text-rose-600 dark:text-rose-400 font-semibold" : c.free <= Math.max(2, Math.round(c.beds * 0.1)) ? "text-amber-700 dark:text-amber-300 font-medium" : "text-foreground/70";
                  const over = c.expected > c.beds;
                  return (
                    <div key={c.date} className={cn("absolute inset-y-0 flex items-center justify-center border-r border-border/40 font-mono text-[11px] tabular", tone, c.today && "bg-primary/[0.05]")} style={{ left: i * DAY, width: DAY }}
                      title={`${dayLabel(c.date)}: ${c.free} ledige av ${c.beds} · ${c.occupied} opptatt${c.expected ? ` · ${c.expected} forventet fra rotasjon` : ""}${over ? " – over kapasitet" : ""}`} data-testid={`calendar-free-${c.date}`}>
                      {c.free}{over && <span className="ml-0.5 h-1 w-1 rounded-full bg-rose-500" aria-hidden />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* body */}
          {groups.map((g) => (
            <div key={g.building} data-testid={`calendar-building-${g.building}`}>
              <div className="flex" style={{ height: HEAD_H - 4 }}>
                <div className="sticky left-0 z-20 flex items-center border-b border-r bg-muted/60 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" style={{ width: LEFT, minWidth: LEFT }}>Bygg {g.building} <span className="ml-1.5 font-mono font-normal">{g.lanes.length}</span></div>
                <div className="relative border-b bg-muted/40" style={{ width }} />
              </div>
              {g.lanes.map((lane) => {
                const r = lane.room;
                const stays = staysByLane.get(lane.key) || [];
                const out = r.room_status === "ute_av_drift";
                const isGhostLane = drag?.lane === lane.index && drag.to;
                const sel = create?.lane === lane.index ? create : null;
                return (
                  <div key={lane.key} className="group/lane flex" style={{ height: ROW_H }} data-testid={`calendar-lane-${r.operational_number}-${lane.bed}`}>
                    <button type="button" onClick={() => onOpenRoom?.(r.room_id)} className={cn("sticky left-0 z-20 flex items-center gap-2 border-b border-r bg-card px-3 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60", out && "bg-rose-500/[0.04]")} style={{ width: LEFT, minWidth: LEFT }} data-testid={`calendar-room-${r.operational_number}-${lane.bed}`}>
                      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", out ? ROOM_DOT.ute_av_drift : r.room_status === "ma_rengjores" ? ROOM_DOT.ma_rengjores : "bg-border")} aria-hidden />
                      <span className="font-mono text-[12.5px] font-semibold tabular">{r.operational_number}</span>
                      {r.beds > 1 && <span className="text-[11px] text-muted-foreground">seng {lane.bed}</span>}
                      {lane.bed === 1 && <span className="ml-auto truncate font-mono text-[10px] text-muted-foreground/70">{r.physical_code}</span>}
                    </button>
                    <div className={cn("relative border-b", out && "bar-planned bg-rose-500/[0.05]", editable && !out && "cursor-crosshair")} style={{ width, height: ROW_H - 1 }} data-lane-index={lane.index}
                      onPointerDown={(e) => onLanePointerDown(e, lane)} onPointerMove={onLanePointerMove} onPointerUp={onLanePointerUp} title={out ? `Ute av drift${r.notes ? ` – ${r.notes}` : ""}` : undefined}>
                      {dayCells}
                      {sel && <div className="absolute inset-y-1 rounded-md border border-dashed border-primary bg-primary/10" style={{ left: sel.from * DAY, width: (sel.to - sel.from + 1) * DAY }} data-testid="calendar-create-selection" />}
                      {stays.map((s) => {
                        const left = x(s.start);
                        const w = Math.max(DAY * 0.6, x(s.end) - left);
                        const dragging = drag?.stay?.id === s.id;
                        const showText = w >= 44;
                        return (
                          <button key={s.id} type="button" data-stay-bar
                            onPointerDown={(e) => onBarPointerDown(e, s, "move")} onPointerMove={onBarPointerMove} onPointerUp={onBarPointerUp}
                            onClick={(e) => { if (!editable) { e.preventDefault(); onOpenRoom?.(s.room_id); } }}
                            className={cn("absolute inset-y-[4px] flex items-center overflow-hidden rounded-md px-1.5 text-left text-[11.5px] leading-none transition-opacity duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              BAR[s.status], (s.overdue_in || s.overdue_out) && "border-amber-500 text-amber-900 dark:text-amber-200", s.overdue_in && "bg-amber-500/[0.10]", s.overdue_out && "bg-amber-500/80 text-background",
                              editable && s.status !== "utsjekket" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer", dragging && "opacity-40")}
                            style={{ left, width: w }} title={stayTitle(s)} aria-label={stayTitle(s)} data-testid={`calendar-stay-${s.id}`}>
                            {showText && <span className="truncate font-medium">{stayLabel(s)}</span>}
                            {showText && w >= 150 && s.employer_name && <span className={cn("ml-1.5 truncate", s.status === "innsjekket" ? "text-background/70" : "text-muted-foreground")}>{s.employer_name}</span>}
                            {editable && s.status === "planlagt" && <span onPointerDown={(e) => onBarPointerDown(e, s, "resize-start")} className="absolute inset-y-0 left-0 w-2 cursor-ew-resize" aria-hidden />}
                            {editable && s.status !== "utsjekket" && <span onPointerDown={(e) => onBarPointerDown(e, s, "resize-end")} className="absolute inset-y-0 right-0 w-2 cursor-ew-resize" aria-hidden data-testid={`calendar-stay-resize-${s.id}`} />}
                          </button>
                        );
                      })}
                      {isGhostLane && (
                        <div className={cn("pointer-events-none absolute inset-y-[3px] rounded-md border-2", drag.conflict ? "border-rose-500 bg-rose-500/15" : "border-primary bg-primary/15")}
                          style={{ left: x(drag.stay.status === "innsjekket" ? drag.stay.start : drag.to.planned_in), width: Math.max(DAY * 0.6, x(drag.to.planned_out) - x(drag.stay.status === "innsjekket" ? drag.stay.start : drag.to.planned_in)) }} data-testid="calendar-drag-ghost">
                          <span className={cn("absolute -top-5 left-0 whitespace-nowrap rounded bg-background px-1.5 py-0.5 text-[10.5px] shadow-sm", drag.conflict ? "text-rose-600" : "text-foreground")}>{drag.conflict ? "Opptatt" : `${drag.to.operational_number} · ${dayLabel(drag.to.planned_in)} – ${dayLabel(drag.to.planned_out)}`}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {!lanes.length && <div className="p-8 text-center text-[13px] text-muted-foreground" data-testid="calendar-empty">Ingen rom på campen i perioden.</div>}
          {/* today line */}
          {todayIdx >= 0 && todayIdx < days && <div className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-primary/70" style={{ left: LEFT + todayIdx * DAY + DAY / 2 }} aria-hidden data-testid="calendar-today-line" />}
        </div>
      </div>

      {/* legend + hint */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-muted-foreground" data-testid="calendar-legend">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-foreground/85" /> Innsjekket</span>
        <span className="inline-flex items-center gap-1.5"><span className="bar-planned h-3 w-5 rounded-sm border border-foreground/40 bg-background" /> Planlagt</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm border border-border bg-muted" /> Utsjekket</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm border border-amber-500 bg-amber-500/20" /> Forsinket</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-rose-500/20" /> Ute av drift</span>
        <span className="ml-auto inline-flex items-center gap-1.5">{editable ? <><MousePointer2 className="h-3.5 w-3.5" /> Dra opphold til annet rom, trekk i kanten for å endre dato, eller dra på en tom rad for å legge inn nytt. Ingenting lagres før du bekrefter.</> : canWrite && !fine ? "Endringer gjøres fra romkortet på berøringsskjerm." : `${num(d.counts.stays)} opphold i perioden`}</span>
      </div>

      <RescheduleDialog pending={pending} onOpenChange={(o) => !o && !m.isPending && setPending(null)} busy={m.isPending} onConfirm={(reason) => m.mutate({ ...pending, reason })} />
    </div>
  );
};
